/**
 * 实体表的客户端筛选 / 排序 / 账户选项。
 *
 * 诚实声明：`/v1/entities` 是**游标分页**且不支持排序参数，所以这里的筛选与排序
 * **只作用于已加载到浏览器的行**。UI 必须把这句话写出来，不能让用户以为在对全量排序。
 *
 * 纯函数、无 React/DOM，边界（空输入、null 预算、未知列）在 entitySelectors.spec.ts 里钉死。
 */

import { ENTITY_COLUMN_BY_ID, type EntityTableRow } from "./entityColumns.js";
import type { AdEntityLevel, AdEntityRow } from "../lib/apiTypes.js";

export interface EntityFilter {
  readonly q: string;
  /** "all" 或具体的 status 值（大小写不敏感比较） */
  readonly status: string;
  /** "all" 或 accountId */
  readonly account: string;
}

export const DEFAULT_ENTITY_FILTER: EntityFilter = { q: "", status: "all", account: "all" };

export type EntitySortDir = "asc" | "desc";

export function matchEntity(row: AdEntityRow, filter: EntityFilter): boolean {
  const keyword = filter.q.trim().toLowerCase();
  if (keyword.length > 0) {
    const haystack = `${row.name} ${row.accountId} ${row.id}`.toLowerCase();
    if (!haystack.includes(keyword)) return false;
  }
  if (filter.status !== "all" && row.status.toUpperCase() !== filter.status.toUpperCase()) return false;
  if (filter.account !== "all" && row.accountId !== filter.account) return false;
  return true;
}

export function filterEntities<T extends AdEntityRow>(
  rows: readonly T[],
  filter: EntityFilter,
): readonly T[] {
  return rows.filter((row) => matchEntity(row, filter));
}

/** 排序；缺失值（null）永远排最后，且不随 asc/desc 翻转（与 selectors.sortAds 同规则）。 */
export function sortEntities<T extends EntityTableRow>(
  rows: readonly T[],
  sortId: string,
  dir: EntitySortDir,
): readonly T[] {
  const column = ENTITY_COLUMN_BY_ID.get(sortId);
  if (column === undefined) return rows;
  const factor = dir === "asc" ? 1 : -1;
  const copy = [...rows];
  copy.sort((left, right) => {
    const a = column.sortValue(left);
    const b = column.sortValue(right);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === "number" && typeof b === "number") return (a - b) * factor;
    if (typeof a === "number") return -1;
    if (typeof b === "number") return 1;
    return a.localeCompare(b, "zh-Hans-CN") * factor;
  });
  return copy;
}

export interface EntityAccountOption {
  readonly id: string;
  readonly label: string;
  readonly rowCount: number;
}

export function entityAccountOptions(rows: readonly AdEntityRow[]): readonly EntityAccountOption[] {
  const map = new Map<string, EntityAccountOption>();
  for (const row of rows) {
    const existing = map.get(row.accountId);
    if (existing === undefined) {
      map.set(row.accountId, { id: row.accountId, label: row.accountId, rowCount: 1 });
    } else {
      map.set(row.accountId, { ...existing, rowCount: existing.rowCount + 1 });
    }
  }
  return [...map.values()].sort((left, right) => left.label.localeCompare(right.label, "en"));
}

/** 已加载行里出现过的状态值（去重、稳定顺序）。 */
export function entityStatusOptions(rows: readonly AdEntityRow[]): readonly string[] {
  const seen = new Set<string>();
  for (const row of rows) seen.add(row.status);
  return [...seen].sort((left, right) => left.localeCompare(right, "en"));
}

/** 当前层级对应的中文说明，给空态/工具栏用。 */
export function entityLevelHint(level: AdEntityLevel): string {
  switch (level) {
    case "campaign":
      return "广告系列（campaign）：支持暂停/启动与预算调整";
    case "adset":
      return "广告组（adset）：支持暂停/启动与预算调整";
    case "ad":
      return "广告（ad）：支持暂停/启动；广告层没有预算字段";
  }
}
